import { Building2, Edit, Eye, Plus, Search, Shield, Trash2, User, Users, Wrench } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import API_BASE_URL, { API_ENDPOINTS, buildApiUrl } from '../config/api.js';
import { useAuth } from '../contexts/AuthContext';
import './Usuarios.css';

const Usuarios = () => {
  const { user, authenticatedFetch } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [viewingEmployee, setViewingEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'STAFF',
    imageUrl: '',
    agencyCode: ''
  });
  const [agencies, setAgencies] = useState([]);





  const roles = [
    { 
      value: 'ADMIN', 
      label: 'Administrador', 
      icon: Shield, 
      color: 'admin',
      permissions: ['Gestionar usuarios', 'Gestionar bicicletas', 'Gestionar reservas', 'Ver informes', 'Acceso completo']
    },
    { 
      value: 'STAFF', 
      label: 'Staff', 
      icon: User, 
      color: 'staff',
      permissions: ['Gestionar reservas', 'Check-in/Check-out', 'Ver inventario']
    },
    { 
      value: 'TECHNICIAN', 
      label: 'Técnico', 
      icon: Wrench, 
      color: 'technician',
      permissions: ['Gestionar mantenimiento', 'Ver inventario', 'Actualizar estado de bicicletas']
    },
    { 
      value: 'AGENCY', 
      label: 'Agencia', 
      icon: Building2, 
      color: 'agency',
      permissions: ['Gestionar reservas de la agencia', 'Ver reservas asociadas', 'Acceso limitado a datos de la agencia']
    }
  ];

  useEffect(() => {
    // Verificar si el usuario es ADMIN
    if (user && user.role === 'ADMIN') {
      fetchEmployees();
      fetchAgencies();
    }
  }, [user]);

  const fetchAgencies = async () => {
    try {
      const response = await authenticatedFetch(buildApiUrl(API_ENDPOINTS.AGENCIES));
      if (response.ok) {
        const data = await response.json();
        // El endpoint puede devolver { data: [...] } o directamente [...]
        setAgencies(Array.isArray(data) ? data : (data.data || []));
      }
    } catch (error) {
      console.error('Error al cargar agencias:', error);
      setAgencies([]);
    }
  };

  // Filtrar usuarios cuando cambie el término de búsqueda
  useEffect(() => {
    const filtered = employees.filter(employee =>
      employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.role.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredEmployees(filtered);
    setCurrentPage(1);
  }, [employees, searchTerm]);

  // Calcular usuarios para la página actual
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentEmployees = filteredEmployees.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      console.log('🔄 Cargando usuarios desde:', buildApiUrl(API_ENDPOINTS.USERS));
      
      const response = await authenticatedFetch(buildApiUrl(API_ENDPOINTS.USERS));
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Usuarios cargados:', data);
        setEmployees(data);
      } else if (response.status === 403) {
        setError('No tienes permisos para acceder a esta funcionalidad');
      } else {
        console.error('❌ Error al cargar usuarios:', response.status, response.statusText);
        setError(`Error al cargar usuarios: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('❌ Error de conexión:', error);
      setError('Error de conexión al servidor. Verifica que el backend esté ejecutándose.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      // Si se cambia el rol y no es AGENCY, limpiar agencyCode
      if (name === 'role' && value !== 'AGENCY') {
        newData.agencyCode = '';
      }
      return newData;
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'STAFF',
      imageUrl: '',
      agencyCode: ''
    });
    setEditingEmployee(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const submitData = { ...formData };
    
    // Si estamos editando, no enviar la contraseña si está vacía
    if (editingEmployee && !submitData.password) {
      delete submitData.password;
    }

    // Si el rol no es AGENCY, no enviar agencyCode
    if (submitData.role !== 'AGENCY') {
      delete submitData.agencyCode;
    }

    try {
      const url = editingEmployee 
        ? buildApiUrl(`${API_ENDPOINTS.USERS}/${editingEmployee.id}`)
        : buildApiUrl(API_ENDPOINTS.USERS);
      
      console.log('🔄 Enviando datos a:', url);
      console.log('📦 Datos:', submitData);
      
      const response = await authenticatedFetch(url, {
        method: editingEmployee ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Usuario guardado exitosamente:', result);
        await fetchEmployees();
        setShowForm(false);
        resetForm();
        alert(editingEmployee ? 'Usuario actualizado exitosamente' : 'Usuario registrado exitosamente');
      } else {
        const errorData = await response.json();
        console.error('❌ Error del servidor:', errorData);
        
        if (response.status === 401) {
          alert('Error: No tienes permisos para realizar esta acción. Verifica tu autenticación.');
        } else if (response.status === 403) {
          alert('Error: Acceso denegado. Solo los administradores pueden gestionar usuarios.');
        } else {
          alert(`Error: ${errorData.message || 'Error al guardar el usuario'}`);
        }
      }
    } catch (error) {
      console.error('❌ Error de conexión:', error);
      alert('Error de conexión al servidor. Verifica que el backend esté ejecutándose.');
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name,
      email: employee.email,
      password: '', // No mostrar contraseña actual
      role: employee.role,
      imageUrl: employee.imageUrl || '',
      agencyCode: employee.agencyCode || ''
    });
    setShowForm(true);
  };

  const handleView = (employee) => {
    setViewingEmployee(employee);
  };

  const handleDelete = async (employeeId) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este usuario?')) {
      return;
    }

    try {
      const response = await authenticatedFetch(buildApiUrl(`${API_ENDPOINTS.USERS}/${employeeId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        await fetchEmployees();
        alert('Usuario eliminado exitosamente');
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.message || 'Error al eliminar el usuario'}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al eliminar el usuario');
    }
  };

  const getRoleLabel = (role) => {
    const roleObj = roles.find(r => r.value === role);
    return roleObj ? roleObj.label : role;
  };

  const getRoleColor = (role) => {
    const roleObj = roles.find(r => r.value === role);
    return roleObj ? roleObj.color : 'default';
  };

  const getRoleIcon = (role) => {
    const roleObj = roles.find(r => r.value === role);
    return roleObj ? roleObj.icon : User;
  };

  // Si el usuario no es ADMIN, mostrar mensaje de acceso denegado
  if (user && user.role !== 'ADMIN') {
    return (
      <div className="usuarios-page">
        <div className="access-denied">
          <Shield size={64} color="#dc3545" />
          <h2>Acceso Denegado</h2>
          <p>Esta funcionalidad solo está disponible para administradores.</p>
          <p>Tu rol actual: <strong>{user.role}</strong></p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="usuarios-page">
        <div className="loading">Cargando usuarios...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="usuarios-page">
        <div className="error-message">
          <h2>Error de Conexión</h2>
          <p>{error}</p>
          <button 
            className="btn-primary"
            onClick={() => {
              setError(null);
              fetchEmployees();
            }}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="usuarios-page">
      <div className="page-header">
        <h1>Gestión de Usuarios</h1>
        <button 
          className="btn-primary"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          <Plus size={16} />
          Nuevo Usuario
        </button>
      </div>

      {/* Buscador y controles */}
      <div className="table-controls">
        <div className="search-container">
          <Search size={16} />
          <input
            type="text"
            placeholder="Buscar por nombre, email o rol..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="pagination-controls">
          <label>
            Mostrar:
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="items-per-page-select"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
            </select>
          </label>
        </div>
      </div>

      <div className="table-container">
        {filteredEmployees.length === 0 ? (
          <div className="no-users">
            {searchTerm ? (
              <p>No se encontraron usuarios que coincidan con "{searchTerm}"</p>
            ) : (
              <p>No hay usuarios registrados</p>
            )}
            {!searchTerm && (
              <button 
                className="btn-primary"
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
              >
                Registrar el primer usuario
              </button>
            )}
          </div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Rol</th>
                  <th>Fecha de Registro</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {currentEmployees.map((employee) => {
                  const RoleIcon = getRoleIcon(employee.role);
                  return (
                    <tr key={employee.id}>
                      <td>#{employee.id}</td>
                      <td>
                        <div className="user-name">
                          {employee.imageUrl && (
                            <img 
                              src={employee.imageUrl} 
                              alt={employee.name}
                              className="user-avatar"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                          <span>{employee.name}</span>
                        </div>
                      </td>
                      <td>{employee.email}</td>
                      <td>
                        <span className={`role-badge ${getRoleColor(employee.role)}`}>
                          <RoleIcon size={14} />
                          {getRoleLabel(employee.role)}
                        </span>
                      </td>
                      <td>{new Date(employee.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-icon"
                            title="Ver detalles"
                            onClick={() => handleView(employee)}
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            className="btn-icon"
                            title="Editar"
                            onClick={() => handleEdit(employee)}
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            className="btn-icon btn-danger"
                            title="Eliminar"
                            onClick={() => handleDelete(employee.id)}
                            disabled={employee.id === user?.id}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Paginación */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="btn-secondary"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Anterior
                </button>
                
                <div className="page-numbers">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      className={`btn ${currentPage === page ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  className="btn-secondary"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Siguiente
                </button>
              </div>
            )}

            {/* Información de resultados */}
            <div className="results-info">
              Mostrando {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredEmployees.length)} de {filteredEmployees.length} usuarios
            </div>
          </>
        )}
      </div>

      {/* Formulario Modal */}
      {showForm && (
        <div className="form-overlay">
          <div className="form-container">
            <div className="form-header">
              <h2>{editingEmployee ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>
              <button 
                className="btn-close"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="user-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Nombre *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Nombre completo"
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="email@ejemplo.com"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Contraseña {editingEmployee ? '' : '*'}</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required={!editingEmployee}
                    placeholder={editingEmployee ? "Dejar vacío para mantener" : "Contraseña"}
                  />
                  {editingEmployee && (
                    <small>Dejar vacío para mantener la contraseña actual</small>
                  )}
                </div>
                <div className="form-group">
                  <label>Rol *</label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    required
                  >
                    {roles.map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                  {formData.role && (
                    <div className="role-permissions">
                      <small><strong>Permisos de este rol:</strong></small>
                      <div className="permissions-preview">
                        {roles.find(r => r.value === formData.role)?.permissions.map((permission, index) => (
                          <span key={index} className="permission-tag small">
                            {permission}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>URL de Imagen (Opcional)</label>
                  <input
                    type="url"
                    name="imageUrl"
                    value={formData.imageUrl}
                    onChange={handleInputChange}
                    placeholder="https://ejemplo.com/imagen.jpg"
                  />
                </div>
                {formData.role === 'AGENCY' && (
                  <div className="form-group">
                    <label>Código de Agencia *</label>
                    <select
                      name="agencyCode"
                      value={formData.agencyCode}
                      onChange={handleInputChange}
                      required={formData.role === 'AGENCY'}
                    >
                      <option value="">Seleccionar agencia</option>
                      {agencies.map(agency => (
                        <option key={agency.id} value={agency.code}>
                          {agency.name} ({agency.code})
                        </option>
                      ))}
                    </select>
                    {formData.role === 'AGENCY' && !formData.agencyCode && (
                      <small style={{ color: '#dc3545' }}>El código de agencia es requerido para usuarios con rol AGENCY</small>
                    )}
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {editingEmployee ? 'Actualizar' : 'Registrar'} Usuario
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Detalles */}
      {viewingEmployee && (
        <div className="form-overlay">
          <div className="form-container">
            <div className="form-header">
              <h2>Detalles del Usuario</h2>
              <button 
                className="btn-close"
                onClick={() => setViewingEmployee(null)}
              >
                ×
              </button>
            </div>

            <div className="user-details">
              <div className="detail-row">
                <strong>ID:</strong> #{viewingEmployee.id}
              </div>
              <div className="detail-row">
                <strong>Nombre:</strong> {viewingEmployee.name}
              </div>
              <div className="detail-row">
                <strong>Email:</strong> {viewingEmployee.email}
              </div>
              <div className="detail-row">
                <strong>Rol:</strong> 
                <span className={`role-badge ${getRoleColor(viewingEmployee.role)}`}>
                  {getRoleLabel(viewingEmployee.role)}
                </span>
              </div>
              {viewingEmployee.agencyCode && (
                <div className="detail-row">
                  <strong>Código de Agencia:</strong> {viewingEmployee.agencyCode}
                </div>
              )}
              <div className="detail-row">
                <strong>Permisos:</strong>
                <div className="permissions-list">
                  {roles.find(r => r.value === viewingEmployee.role)?.permissions.map((permission, index) => (
                    <span key={index} className="permission-tag">
                      {permission}
                    </span>
                  ))}
                </div>
              </div>
              <div className="detail-row">
                <strong>Fecha de Registro:</strong> {new Date(viewingEmployee.createdAt).toLocaleDateString()}
              </div>
              <div className="detail-row">
                <strong>Última Actualización:</strong> {new Date(viewingEmployee.updatedAt).toLocaleDateString()}
              </div>
            </div>

            <div className="form-actions">
              <button 
                className="btn-secondary" 
                onClick={() => setViewingEmployee(null)}
              >
                Cerrar
              </button>
              <button 
                className="btn-primary"
                onClick={() => {
                  setViewingEmployee(null);
                  handleEdit(viewingEmployee);
                }}
              >
                Editar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Usuarios;

